import { useState } from "react";
import type { CareNavigationRosterResponse } from "@t3tools/contracts";
import { useQuery } from "@tanstack/react-query";
import { Button } from "~/components/ui/button";
import {
  CareNavigationNavigatorFilter,
  CareNavigationTeamFilter,
  CareNavigationStaffStatusFilter,
} from "./careNavigationNavigatorFilter";
import {
  careNavigationTeamNavigators,
  careNavigationAccountId,
  careNavigationSelectedMembers,
  careNavigationScopeMembers,
  careNavigationTeams,
  type CareNavigationTeamId,
  type CareNavigationStaffStatus,
} from "./careNavigationTeams";
import { careNavigationRosterQueryOptions } from "./dashboardsReactQuery";
import { CareNavigationRoster } from "./careNavigationRoster";
import {
  careNavigationCarePlansQueryOptions,
  type CarePlansResponse,
} from "./careNavigationCarePlansQuery";
import { CarespaceCountLink } from "./carespaceCountLink";
import { CarespaceRecordsProvider } from "./carespaceRecords";
import { DataTable, DashboardsPageFrame } from "./dashboardKit";

export function CareNavigationCarePlansView(props: {
  data?: CarePlansResponse | undefined;
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
  roster: CareNavigationRosterResponse | undefined;
  rosterError?: boolean;
}) {
  const [team, setTeam] = useState<CareNavigationTeamId>();
  const [navigators, setNavigators] = useState<string[]>([]);
  const [staffStatus, setStaffStatus] = useState<CareNavigationStaffStatus>("active");
  const members = props.roster?.members ?? [];
  const available = careNavigationTeamNavigators(undefined, members, staffStatus);
  const selected = team ? careNavigationTeamNavigators(team, members, staffStatus) : navigators;
  const selectedIds = new Set(
    careNavigationSelectedMembers(
      members,
      team,
      staffStatus,
      navigators,
      props.roster?.unmatchedNavigatorNames,
    ).map((member) => member.navigatorUserId),
  );
  const inScope = (row: { navigatorUserId?: string; navigatorName: string }) => {
    const id = careNavigationAccountId(row, members, props.roster?.unmatchedNavigatorNames);
    return !props.rosterError && id !== undefined && selectedIds.has(id);
  };
  const workload = (props.data?.workload ?? []).filter(inScope);
  const outstanding = (props.data?.outstanding ?? []).filter(inScope);
  function changeTeam(next: CareNavigationTeamId | undefined) {
    setTeam(next);
    setNavigators([]);
  }
  function changeNavigators(next: string[]) {
    setTeam(undefined);
    setNavigators(next);
  }

  return (
    <CarespaceRecordsProvider
      filePrefix="care-navigation-care-plans"
      records={outstanding.flatMap((row) => row.records)}
    >
      <DashboardsPageFrame className="space-y-6">
        <header className="space-y-4 rounded-lg border border-border/70 bg-background p-5">
          <h1 className="text-2xl font-semibold">Care Navigation · Care plans</h1>
          <p className="text-sm text-muted-foreground">
            Current aligned caseload. These tables are independent of the monthly completion
            criteria.
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <CareNavigationTeamFilter
              team={team}
              teams={careNavigationTeams(members)}
              selected={navigators}
              onChange={changeTeam}
            />
            <CareNavigationStaffStatusFilter
              value={staffStatus}
              onChange={(status) => {
                setStaffStatus(status);
                setNavigators([]);
              }}
            />
            <CareNavigationNavigatorFilter
              options={available}
              selected={selected}
              onChange={changeNavigators}
            />
            <Button variant="outline" size="sm" onClick={props.onRefresh} disabled={props.loading}>
              Refresh
            </Button>
          </div>
          {props.data && props.roster && !props.rosterError ? (
            <p className="text-xs text-muted-foreground">
              As of {new Date(props.data.asOf).toLocaleString()} · refreshed every minute
            </p>
          ) : null}
        </header>
        {props.rosterError ? (
          <p role="alert">The Care Navigation directory is unavailable. Try refreshing.</p>
        ) : !props.roster ? (
          <p role="status">Loading team directory…</p>
        ) : (
          <CareNavigationRoster
            roster={props.roster}
            members={careNavigationScopeMembers(members, team, staffStatus)}
          />
        )}
        {props.loading ? <p role="status">Loading care plan data…</p> : null}
        {props.error ? (
          <p role="alert">Care plan data is temporarily unavailable. Try refreshing.</p>
        ) : null}
        {props.data?.cache === "STALE" ? (
          <p role="status">
            Showing previously loaded care plan data while refresh is unavailable.
          </p>
        ) : null}
        {props.data && props.roster && !props.rosterError ? (
          <>
            <section
              className="space-y-3 rounded-lg border border-border/70 bg-background p-4"
              aria-label="Goals and interventions by care navigator"
            >
              <h2 className="text-lg font-semibold">Goals and interventions by care navigator</h2>
              <p className="text-sm text-muted-foreground">
                Averages per aligned Carespace with at least one goal or intervention activity.
                Includes all activity statuses, including completed and deleted items. Software-only
                organizations are excluded.
              </p>
              <DataTable
                rows={workload}
                rowKey={(row) => row.navigatorUserId ?? row.navigatorName}
                defaultSort={{ key: "avgGoalsPerCarespace", direction: "desc" }}
                emptyMessage="No goal or intervention activity for the selected navigators."
                columns={[
                  { key: "navigatorName", label: "Care navigator", sortable: true },
                  {
                    key: "carespacesWithGoals",
                    label: "Carespaces with goals / interventions",
                    align: "right",
                    sortable: true,
                  },
                  {
                    key: "avgGoalsPerCarespace",
                    label: "Avg goals per Carespace",
                    align: "right",
                    sortable: true,
                    render: (row) => row.avgGoalsPerCarespace.toFixed(2),
                  },
                  {
                    key: "avgInterventionsPerCarespace",
                    label: "Avg interventions per Carespace",
                    align: "right",
                    sortable: true,
                    render: (row) => row.avgInterventionsPerCarespace.toFixed(2),
                  },
                ]}
              />
            </section>
            <section
              className="space-y-3 rounded-lg border border-border/70 bg-background p-4"
              aria-label="Needs care plan update"
            >
              <h2 className="text-lg font-semibold">Needs care plan update</h2>
              <p className="text-sm text-muted-foreground">
                Matches the care-plan-only tasks in Home → Requires Care This Month: aligned,
                unarchived Low-tier Carespaces whose prior one-on-one covers this month and whose
                care plan still needs an update. Comment-only saves do not clear this requirement.
                Open a count to review the Carespaces.
              </p>
              <DataTable
                rows={outstanding}
                rowKey={(row) => row.navigatorUserId ?? row.navigatorName}
                defaultSort={{ key: "count", direction: "desc" }}
                emptyMessage="No Carespaces need a care plan update for the selected navigators."
                columns={[
                  { key: "navigatorName", label: "Care navigator", sortable: true },
                  {
                    key: "count",
                    label: "Needs care plan update",
                    align: "right",
                    sortable: true,
                    render: (row) => (
                      <CarespaceCountLink
                        count={row.count}
                        recordIds={row.carespaceIds}
                        label={`${row.navigatorName} · needs care plan update`}
                      />
                    ),
                  },
                ]}
              />
            </section>
          </>
        ) : null}
      </DashboardsPageFrame>
    </CarespaceRecordsProvider>
  );
}

export function CareNavigationCarePlans() {
  const query = useQuery(careNavigationCarePlansQueryOptions());
  const rosterQuery = useQuery(careNavigationRosterQueryOptions());
  return (
    <CareNavigationCarePlansView
      data={query.isError ? undefined : query.data}
      loading={query.isPending}
      error={query.isError}
      roster={rosterQuery.data}
      rosterError={rosterQuery.isError}
      onRefresh={() => {
        void query.refetch();
        if (rosterQuery.isError) void rosterQuery.refetch();
      }}
    />
  );
}
